export const SOURCE_ID_KEY = "amai_chat_source_id";

/**
 * Returns a stable per-visitor identifier persisted in localStorage. If
 * no id exists yet, generates one and stores it. This is what ties the
 * visitor across page reloads and what the backend uses as the contact's
 * `channel_handles.web`.
 */
export function getOrCreateSourceId(): string {
    try {
        const existing = localStorage.getItem(SOURCE_ID_KEY);
        if (existing) return existing;
        const fresh = randomId();
        localStorage.setItem(SOURCE_ID_KEY, fresh);
        return fresh;
    } catch {
        // localStorage disabled (private mode, embedded contexts).
        return randomId();
    }
}

export function clearSourceId(): void {
    try {
        localStorage.removeItem(SOURCE_ID_KEY);
    } catch {
        // ignore
    }
}

export const CHAT_BEGUN_KEY = "amai_chat_begun";

/**
 * Whether this browser saw the chat of visitor `sourceId` begin (they
 * sent a message, or their history had one). Only a guess for the next
 * page: a chat window in it opens at full height right away instead of
 * once its history is in, and the history settles it. Stored as the
 * source id, so a new visitor id (reset) starts without it.
 */
export function hasBegunChat(sourceId: string): boolean {
    try {
        return localStorage.getItem(CHAT_BEGUN_KEY) === sourceId;
    } catch {
        return false;
    }
}

export function rememberChatBegun(sourceId: string, begun: boolean): void {
    try {
        if (begun) localStorage.setItem(CHAT_BEGUN_KEY, sourceId);
        else localStorage.removeItem(CHAT_BEGUN_KEY);
    } catch {
        // localStorage disabled — the window then waits for the history.
    }
}

export const TEASER_SEEN_KEY = "amai_chat_teaser_seen";

/**
 * Whether the teaser bubble is done for this browser session: it was
 * shown once already, or the visitor opened the chat (then there is
 * nothing left to tease). sessionStorage, so a new session — a new tab
 * or a restarted browser — gets the teaser again. When the storage can't
 * be read we report "seen": better no teaser than one on every page.
 */
export function hasSeenTeaser(): boolean {
    try {
        return sessionStorage.getItem(TEASER_SEEN_KEY) === "1";
    } catch {
        return true;
    }
}

export function markTeaserSeen(): void {
    try {
        sessionStorage.setItem(TEASER_SEEN_KEY, "1");
    } catch {
        // sessionStorage disabled or full — nothing to remember it in.
    }
}

function randomId(): string {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
    }
    return "src-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}
